/**
 * Contact-candidates client (2026-08 finish line): the modal's real send, the
 * per-candidate status hydration behind the "Contacted" chips, and the
 * message-history drawer state + paged list.
 *
 * NgZone note: Apollo v4 delivers results OUTSIDE Angular's zone (bug class
 * of 617f096). This service stays zone-agnostic (plain promises + subjects);
 * every consumer applies results inside zone.run + detectChanges, matching
 * SavedSearchesService.
 */
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Subject, firstValueFrom, type Observable } from 'rxjs';
import { MAPS_CONTACT_HISTORY, MAPS_CONTACT_SEND, MAPS_CONTACT_STATUS } from './maps-graphql/queries';
import type {
    MapsContactCandidateStatus,
    MapsContactHistoryPage,
    MapsContactSendInput,
    MapsContactSendResult,
} from '../models/maps.types';

@Injectable({ providedIn: 'root' })
export class ContactService {
    /** Message-history drawer open/closed (the kebab menu's Messages item
     *  toggles it; the header hosts the drawer so both hosts get it free). */
    private readonly historyDrawerOpen$ = new BehaviorSubject<boolean>(false);
    /** Fires after every completed send — the search page patches its status
     *  chips optimistically and re-hydrates. */
    private readonly sends$ = new Subject<MapsContactSendResult>();

    constructor(private readonly apollo: Apollo) {}

    get HistoryDrawerOpen$(): Observable<boolean> {
        return this.historyDrawerOpen$.asObservable();
    }

    get Sends$(): Observable<MapsContactSendResult> {
        return this.sends$.asObservable();
    }

    OpenHistoryDrawer(): void {
        this.historyDrawerOpen$.next(true);
    }

    CloseHistoryDrawer(): void {
        this.historyDrawerOpen$.next(false);
    }

    /** Send the composed message to the selected candidates. The server
     *  resolves recipient emails from the owned search snapshot and enforces
     *  the delivery-safety mode — a success here means the relay accepted the
     *  batch under that mode, NOT necessarily that members received email. */
    async Send(input: MapsContactSendInput): Promise<MapsContactSendResult> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsContactSend: MapsContactSendResult }>({
                mutation: MAPS_CONTACT_SEND,
                variables: { input },
            })
        );
        if (!result.data?.mapsContactSend) {
            throw new Error('mapsContactSend returned empty payload');
        }
        this.sends$.next(result.data.mapsContactSend);
        return result.data.mapsContactSend;
    }

    /** Batched per-candidate rollups for one result set (never per card). */
    async Statuses(candidateIds: readonly string[]): Promise<MapsContactCandidateStatus[]> {
        if (candidateIds.length === 0) return [];
        const result = await firstValueFrom(
            this.apollo.query<{ mapsContactStatus: MapsContactCandidateStatus[] }>({
                query: MAPS_CONTACT_STATUS,
                variables: { candidateIds: [...candidateIds] },
                fetchPolicy: 'network-only',
            })
        );
        if (!result.data) throw new Error('Contact status query returned no data');
        return result.data.mapsContactStatus;
    }

    /** Newest-first company-scoped history page for the drawer. */
    async History(limit = 50, offset = 0): Promise<MapsContactHistoryPage> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsContactHistory: MapsContactHistoryPage }>({
                query: MAPS_CONTACT_HISTORY,
                variables: { limit, offset },
                fetchPolicy: 'network-only',
            })
        );
        if (!result.data) throw new Error('Contact history query returned no data');
        return result.data.mapsContactHistory;
    }
}
